import { useEffect, useState, useCallback, useRef } from "react";
import { X, ChevronLeft, ChevronRight, Sparkles, CheckCircle2, Map, LayoutDashboard, ClipboardList, Bike, Package, ScanLine, Wrench, Receipt, Bot, PlayCircle } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "./ui/button";

/**
 * Aug-2026 (User-Wunsch Jörg) — Onboarding-Tour komplett überarbeitet:
 *  • Kapitel je Bereich (Dashboard, Aufgaben, Dispo, WMS, Scan, Werkstatt, …)
 *  • Kapitel-Auswahl beim Start (einzelnes Kapitel ODER Komplett-Tour)
 *  • Seitenübergreifend: die Tour navigiert automatisch zur jeweiligen Seite
 *  • Eigene Tour für Mitarbeiter-Konten (zweiradverkaeufer)
 * Schritte ohne `selector` werden als zentrierte Info-Karte gezeigt.
 */
const STORAGE_PREFIX = "twowheels.tour.completed.";

const DEALER_CHAPTERS = [
    {
        id: "dashboard", title: "Dashboard & Briefing", icon: LayoutDashboard,
        steps: [
            { url: "/dashboard", title: "Dein Morgen-Briefing", body: "Oben begrüßt dich das Briefing mit den Kennzahlen deines Standorts: offene Team-Aufgaben, überfällige Aufgaben, E-Mail-Freigaben und der Scan-Report von gestern. Jeder Chip ist klickbar und springt direkt zum passenden Bereich." },
            { url: "/dashboard", selector: "[data-testid='tasks-hints-tab-hints']", placement: "bottom", title: "Was zu tun ist", body: "Unter „Hinweise“ sammelt TwoWheels alles Dringende: fällige Werkstatt-Termine, nicht zugeordnete SumUp-Zahlungen, Probefahrt-Anfragen, Dokumenten-Überwachung u. v. m. — vieles direkt mit 1-Klick-Aktionen erledigbar." },
            { url: "/dashboard", selector: "[data-testid='cockpit-customize-btn']", placement: "top", title: "Cockpit anpassen", body: "Stelle dein Cockpit pro Arbeitsbereich (Verkauf · Ankauf · Werkstatt) ein — Widgets ein-/ausblenden und per Drag & Drop sortieren." },
        ],
    },
    {
        id: "tasks", title: "Aufgaben & Team", icon: ClipboardList,
        steps: [
            { url: "/dashboard?tab=tasks", title: "Meine Aufgaben", body: "Hier stehen alle Aufgaben, die dir zugewiesen sind — mit Fälligkeit, Snooze und „alle erledigen“. Erledigte Aufgaben verschwinden aus den Zählern im Briefing." },
            { url: "/dashboard?tab=workload", title: "Mitarbeiter-Auslastung", body: "Die Team-Ansicht zeigt je Mitarbeiter deiner Filiale offene und überfällige Aufgaben plus Anwesenheit. Klick auf einen Mitarbeiter öffnet seine Aufgaben, über „Zuweisen“ verteilst du neue." },
            { url: "/dashboard?tab=email", title: "E-Mail-Freigabe", body: "Ausgehende System-E-Mails warten hier auf deine Freigabe — inkl. Fahrzeug-Nr., Verkäufer und Verkaufsdatum. Nichts geht ohne deinen Klick raus." },
        ],
    },
    {
        id: "listings", title: "Inserate & Dispo", icon: Bike,
        steps: [
            { url: "/dashboard?tab=listings", selector: "[data-testid='new-listing-btn']", placement: "left", title: "Neues Inserat", body: "Mit einem Klick in den geführten Modus — die KI füllt 80 % der Felder per ZB1-Scan automatisch aus." },
            { url: "/dashboard?tab=listings", selector: "[data-testid='inventory-cockpit']", placement: "bottom", title: "Inventar-Cockpit", body: "Frisch / Reifend / Stale — ein Klick auf eine Pille filtert deine Inserate. Liegengebliebene Festpreise stellst du per KI gesammelt auf Auktion um." },
            { url: "/dealer/disposition", title: "Disposition", body: "Die Dispo ist deine Fahrzeug-Zentrale: Reservierungen mit 72h-Countdown, vorläufige Verkäufe mit Verkäufer & Verkaufsdatum, Kaufverträge, Preisschilder, QR-Codes und Transporte — alles je Fahrzeugkarte." },
        ],
    },
    {
        id: "wms", title: "Warenwirtschaft & Etiketten", icon: Package,
        steps: [
            { url: "/dealer/wms", title: "Artikel & Bestand", body: "Alle Ersatzteile mit Bestand, EK/VK und Lagerort. Über das Etikett-Symbol je Zeile druckst du sofort ein Preisetikett oder legst den Artikel auf den Etikettenbogen." },
            { url: "/dealer/wms?tab=labels", title: "Preisetiketten", body: "Etikettenbogen mit Formatwahl (z. B. Zweckform 3660), Stückzahlen und Startposition — ideal, um angefangene Bögen weiterzubedrucken." },
            { url: "/dealer/wms?tab=reorder", title: "Bestellliste & Rückstände", body: "Artikel unter Mindestbestand landen automatisch hier. Das Rückstands-Radar bündelt alle offenen Rückstandsteile mit Alter und Lieferant." },
        ],
    },
    {
        id: "scan", title: "Beleg-Scan & Dokumente", icon: ScanLine,
        steps: [
            { url: "/dealer/wms/scan-delivery", title: "Lieferschein scannen", body: "PDF/Foto hochladen oder per Handy-QR scannen — die KI erkennt Positionen, Rückstände und Kommissionsvermerke (Kunde/Auftrag) und bucht mit einem Klick ein. CEVA-Lieferungen werden als Garantieteile erkannt." },
            { url: "/dealer/documents", selector: "[data-testid='outbox-mobile-qr']", placement: "left", title: "Dokumenten-Center + Handy-QR", body: "EIN überwachter Desktop-Ordner für alle Dokumente — die KI erkennt automatisch Lieferanten-Beleg vs. Kunden-Dokument und verarbeitet passend. Über den Filial-QR lädst du Belege direkt vom Handy hoch — ohne Login, ausdruckbar als Aushang." },
        ],
    },
    {
        id: "workshop", title: "Werkstatt", icon: Wrench,
        steps: [
            { url: "/dealer/workshop-orders", title: "Werkstatt-Aufträge", body: "Angebot → Auftrag → Abrechnung: Lagerteile werden bei Fertigstellung automatisch abgebucht. Tabs für Ersatzfahrzeuge, offene Auslieferungen, offene Garantien, Kunden und Fahrzeuge." },
            { url: "/dealer/workshop-orders", title: "Offene Garantien & interne Aufträge", body: "Nicht zugeordnete Garantieteile (z. B. aus CEVA-Scans) warten im Tab „Offene Garantien“ auf die 1-Klick-Zuordnung. Interne Aufträge (Auslieferung/GW/Garantie intern) versenden KEINE automatischen Kunden-Mails — nur manuell." },
            { url: "/dealer/workshop-orders", title: "Termin-Mails", body: "Termin-Bestätigungen und Erinnerungen signieren automatisch mit der Kundendienstannahme deines Standorts (Telefon + E-Mail) — nicht mit dem eingeloggten Nutzer." },
        ],
    },
    {
        id: "billing", title: "Rechnungen & Buchhaltung", icon: Receipt,
        steps: [
            { url: "/dealer/invoices", title: "Rechnungen & Kunden", body: "Rechnungen erstellen (mit Gutschein & eigenem Gesamtbetrag), Kunden- und Fahrzeugverwaltung — der Fahrzeuge-Tab startet direkt auf deinen Kundenfahrzeugen." },
            { url: "/dealer/buchhaltung", title: "Buchhaltung & SumUp", body: "EC-/SumUp-Zahlungen werden nächtlich synchronisiert und automatisch Rechnungen zugeordnet — Unklares landet zur manuellen Zuordnung hier, jüngste zuerst." },
        ],
    },
    {
        id: "ai", title: "KI & Quick-Actions", icon: Bot,
        steps: [
            { url: "/dashboard", selector: "[data-testid='command-palette-fab']", placement: "left", title: "Quick-Actions ⌘K", body: "Jede Funktion in einem Tastendruck: Inserat anlegen, ZB1 scannen, Kunde suchen — einfach ⌘K drücken oder den Button klicken." },
            { url: "/dashboard", title: "KI überall", body: "Der KI-Assistent beantwortet Fragen zu deinen Daten, der Dokumenten-Scan erkennt Belege, die Artikel-Anreicherung zieht Ersatzteil-Daten von Hersteller-Seiten — und nachts um 2 Uhr prüft ein Konsistenz-Check alle Dashboard-Angaben." },
        ],
    },
];

const EMPLOYEE_CHAPTERS = [
    {
        id: "dashboard", title: "Dashboard & Briefing", icon: LayoutDashboard,
        steps: [
            { url: "/dashboard", title: "Dein Briefing", body: "Nach dem Login siehst du dein Briefing mit allem, was heute ansteht: Aufgaben, Hinweise und der Scan-Report deines Standorts." },
            { url: "/dashboard?tab=tasks", title: "Meine Aufgaben", body: "Alle dir zugewiesenen Aufgaben mit Fälligkeit. Erledigtes abhaken — dein Filialleiter sieht den Fortschritt in der Team-Auslastung." },
        ],
    },
    {
        id: "wms", title: "Warenwirtschaft", icon: Package,
        steps: [
            { url: "/dealer/wms", title: "Artikel & Bestand", body: "Ersatzteile suchen, Bestände prüfen, Lagerorte finden. Über das Etikett-Symbol je Artikel druckst du sofort ein Preisetikett." },
            { url: "/dealer/wms?tab=labels", title: "Preisetiketten", body: "Etikettenbogen zusammenstellen, Format wählen (z. B. Zweckform 3660) und mit Startposition auf angefangenen Bögen weiterdrucken." },
        ],
    },
    {
        id: "scan", title: "Beleg-Scan", icon: ScanLine,
        steps: [
            { url: "/dealer/wms/scan-delivery", title: "Lieferschein scannen", body: "PDF/Foto hochladen oder den QR mit dem Handy scannen — die KI erkennt die Positionen und du buchst mit einem Klick ein. Rückstände und Garantieteile (CEVA) werden automatisch erkannt." },
        ],
    },
    {
        id: "workshop", title: "Werkstatt", icon: Wrench,
        steps: [
            { url: "/dealer/workshop-orders", title: "Werkstatt-Aufträge", body: "Aufträge anlegen (Schnell-/Scan-Erfassung), Termine im Kalender, offene Auslieferungen und Garantien im Blick behalten." },
        ],
    },
    {
        id: "ai", title: "Quick-Actions", icon: Bot,
        steps: [
            { url: "/dashboard", selector: "[data-testid='command-palette-fab']", placement: "left", title: "Quick-Actions ⌘K", body: "Mit ⌘K (oder Klick auf den Button) erreichst du jede Funktion in einem Tastendruck." },
        ],
    },
];

const SIMPLE_CHAPTERS = {
    b2c: [{
        id: "basics", title: "Dein Dashboard", icon: LayoutDashboard,
        steps: [
            { url: "/dashboard", selector: "[data-testid='tab-messages']", placement: "bottom", title: "Nachrichten & Anfragen", body: "Hier laufen deine Nachrichten mit Händlern & Verkäufern zusammen — Anfragen zu Inseraten und Antworten auf deine Preisvorschläge." },
            { url: "/dashboard?tab=saved-searches", selector: "[data-testid='tab-saved-searches']", placement: "bottom", title: "Preis-Wecker / Suchen", body: "Speichere Filter-Kombinationen — wir benachrichtigen dich, sobald ein passendes Inserat live geht." },
            { url: "/dashboard?tab=favorites", selector: "[data-testid='tab-favorites']", placement: "bottom", title: "Favoriten", body: "Merke dir Fahrzeuge mit einem Klick. Vergleiche bis zu 3 Stück nebeneinander." },
            { url: "/dashboard", selector: "[data-testid='command-palette-fab']", placement: "left", title: "Quick-Actions ⌘K", body: "Mit ⌘K erreichst du jede Funktion in einem Tastendruck — Suche, Vergleich, KI-Features, Ankaufsanfrage." },
        ],
    }],
    vermittler: [{
        id: "basics", title: "Vermittler-Cockpit", icon: LayoutDashboard,
        steps: [
            { url: "/vermittler", selector: "[data-testid='vermittler-totals']", placement: "bottom", title: "Dein Punkte-Stand", body: "Erhaltene, verfügbare und ausgezahlte €Punkte auf einen Blick." },
            { url: "/vermittler", selector: "[data-testid='highlights-card']", placement: "bottom", title: "Highlights des Monats", body: "Best-Ever, Konvertierungs-Rate und Top-Tipp — mit Konfetti bei neuen Rekorden." },
            { url: "/vermittler", selector: "[data-testid='manual-referral-form']", placement: "top", title: "Empfehlung erfassen", body: "Erfasse Kunden, denen du TwoWheels empfohlen hast — bei erfolgreichem Geschäft gibt es Punkte." },
            { url: "/vermittler", selector: "[data-testid='smily-report-card']", placement: "top", title: "Smily-Aktion", body: "Fahrzeug am Straßenrand entdeckt? Foto + Smily-Aufkleber + Standort, fertig." },
        ],
    }],
    service_provider: [{
        id: "basics", title: "Dienstleister-Dashboard", icon: LayoutDashboard,
        steps: [
            { url: "/dienstleister/dashboard", selector: "[data-testid='club-members-card']", placement: "bottom", title: "Mitglieder & Code-Versand", body: "Erfasse Vereinsmitglieder und versende deinen Transaktionscode per E-Mail, WhatsApp oder SMS — auch als Bulk-Versand." },
            { url: "/dienstleister/dashboard", selector: "[data-testid='command-palette-fab']", placement: "left", title: "Quick-Actions ⌘K", body: "Mit ⌘K erreichst du jede Funktion in einem Tastendruck." },
        ],
    }],
};

function chaptersForUser(user) {
    const role = user?.role;
    if (role === "b2b") return DEALER_CHAPTERS;
    if (role === "zweiradverkaeufer") return EMPLOYEE_CHAPTERS;
    return SIMPLE_CHAPTERS[role] || null;
}

export default function OnboardingTour({ user }) {
    const role = user?.role;
    const userId = user?.id;
    const chapters = chaptersForUser(user);
    const [picker, setPicker] = useState(false);
    const [active, setActive] = useState(false);
    const [fullTour, setFullTour] = useState(false);
    const [chIdx, setChIdx] = useState(0);
    const [stepIdx, setStepIdx] = useState(0);
    const [rect, setRect] = useState(null);
    const navRef = useRef("");
    const navigate = useNavigate();
    const loc = useLocation();

    useEffect(() => {
        if (!userId || !chapters) return;
        const flag = `${STORAGE_PREFIX}${role}`;
        const completed = (() => { try { return localStorage.getItem(flag) === "1"; } catch { return false; } })();
        const onStart = () => { setPicker(true); setActive(false); };
        window.addEventListener("twowheels:start-tour", onStart);
        let t = null;
        let cancelled = false;
        if (!completed) {
            // Auto-Start nur im Live-Betrieb (Produktion)
            fetch(`${process.env.REACT_APP_BACKEND_URL}/api/system/flags`)
                .then((r) => r.json())
                .then((f) => { if (!cancelled && f?.is_live) { t = setTimeout(onStart, 1500); } })
                .catch(() => { /* im Zweifel NICHT auto-starten */ });
        }
        return () => {
            cancelled = true;
            if (t) clearTimeout(t);
            window.removeEventListener("twowheels:start-tour", onStart);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId, role]);

    const chapter = chapters?.[chIdx];
    const step = chapter?.steps?.[stepIdx];
    const totalSteps = (chapters || []).reduce((n, c) => n + c.steps.length, 0);
    const doneSteps = (chapters || []).slice(0, chIdx).reduce((n, c) => n + c.steps.length, 0) + stepIdx;

    const measure = useCallback(() => {
        if (!step) return;
        if (!step.selector) { setRect(null); return; }
        const el = document.querySelector(step.selector);
        if (!el) { setRect(null); return; }
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        setTimeout(() => {
            const r = el.getBoundingClientRect();
            setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
        }, 400);
    }, [step]);

    // Seitenübergreifend: automatisch zur Seite des Schritts navigieren
    useEffect(() => {
        if (!active || !step) return;
        const target = step.url;
        if (target && loc.pathname + loc.search !== target && navRef.current !== `${chIdx}:${stepIdx}`) {
            navRef.current = `${chIdx}:${stepIdx}`;
            navigate(target);
            return;
        }
        const t = setTimeout(measure, 600);
        const onResize = () => measure();
        window.addEventListener("resize", onResize);
        window.addEventListener("scroll", onResize, true);
        return () => {
            clearTimeout(t);
            window.removeEventListener("resize", onResize);
            window.removeEventListener("scroll", onResize, true);
        };
    }, [active, step, loc, navigate, measure, chIdx, stepIdx]);

    const markCompleted = () => { try { localStorage.setItem(`${STORAGE_PREFIX}${role}`, "1"); } catch { /* ignore */ } };
    const quit = () => { markCompleted(); setActive(false); setPicker(false); setRect(null); };
    const startChapter = (idx, full = false) => {
        setFullTour(full); setChIdx(idx); setStepIdx(0); setRect(null);
        navRef.current = ""; setPicker(false); setActive(true);
    };
    const next = () => {
        setRect(null);
        if (stepIdx < chapter.steps.length - 1) { setStepIdx((i) => i + 1); return; }
        if (fullTour && chIdx < chapters.length - 1) { setChIdx((i) => i + 1); setStepIdx(0); return; }
        markCompleted(); setActive(false); setPicker(true);
    };
    const prev = () => {
        setRect(null);
        if (stepIdx > 0) { setStepIdx((i) => i - 1); return; }
        if (fullTour && chIdx > 0) { const c = chapters[chIdx - 1]; setChIdx((i) => i - 1); setStepIdx(c.steps.length - 1); }
    };

    if (!chapters) return null;

    // ---------- Kapitel-Auswahl ----------
    if (picker && !active) {
        return (
            <div className="fixed inset-0 z-[70] bg-black/70 flex items-center justify-center p-4" data-testid="tour-chapter-picker">
                <div className="bg-white border-2 border-[#FFCC00] rounded-sm shadow-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto">
                    <div className="px-5 py-4 bg-[#FFCC00]/15 border-b border-[#FFCC00]/40 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <Map className="w-5 h-5 text-[#111827]" />
                            <h2 className="font-display font-black text-lg">Geführte Tour</h2>
                        </div>
                        <button onClick={quit} className="text-gray-500 hover:text-[#111827]" data-testid="tour-picker-close"><X className="w-5 h-5" /></button>
                    </div>
                    <div className="p-5 space-y-2">
                        <p className="text-sm text-gray-600 mb-3">Wähle ein Kapitel — oder starte die komplette Tour. Die Tour wechselt automatisch auf die passenden Seiten.</p>
                        <button onClick={() => startChapter(0, true)}
                            className="w-full flex items-center gap-3 bg-[#111827] text-[#FFCC00] rounded-sm px-4 py-3 font-bold uppercase tracking-wider text-sm hover:bg-black"
                            data-testid="tour-start-full">
                            <PlayCircle className="w-5 h-5" /> Komplette Tour starten
                            <span className="ml-auto font-mono-num text-xs opacity-80">{totalSteps} Schritte</span>
                        </button>
                        <div className="pt-2 space-y-1.5">
                            {chapters.map((c, i) => {
                                const Icon = c.icon || Sparkles;
                                return (
                                    <button key={c.id} onClick={() => startChapter(i, false)}
                                        className="w-full flex items-center gap-3 border border-gray-200 rounded-sm px-4 py-2.5 text-left hover:border-[#FFCC00] hover:bg-[#FFCC00]/5 transition-colors"
                                        data-testid={`tour-chapter-${c.id}`}>
                                        <Icon className="w-4 h-4 text-gray-600 shrink-0" />
                                        <span className="text-sm font-bold text-gray-900">{i + 1}. {c.title}</span>
                                        <span className="ml-auto text-[11px] text-gray-400 font-mono-num">{c.steps.length} Schritte</span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!active || !step) return null;

    // ---------- Tooltip-Position ----------
    const W = window.innerWidth, H = window.innerHeight;
    const cardW = 380, cardH = 230, gap = 16;
    let ttStyle = { top: H / 2 - cardH / 2, left: Math.max(20, W / 2 - cardW / 2) };
    if (rect) {
        const place = step.placement || "bottom";
        if (place === "bottom") ttStyle = { top: Math.min(H - cardH - 20, rect.top + rect.height + gap), left: Math.max(20, Math.min(W - cardW - 20, rect.left + rect.width / 2 - cardW / 2)) };
        else if (place === "top") ttStyle = { top: Math.max(20, rect.top - cardH - gap), left: Math.max(20, Math.min(W - cardW - 20, rect.left + rect.width / 2 - cardW / 2)) };
        else if (place === "left") ttStyle = { top: Math.max(20, Math.min(H - cardH - 20, rect.top + rect.height / 2 - cardH / 2)), left: Math.max(20, rect.left - cardW - gap) };
        else if (place === "right") ttStyle = { top: Math.max(20, Math.min(H - cardH - 20, rect.top + rect.height / 2 - cardH / 2)), left: Math.min(W - cardW - 20, rect.left + rect.width + gap) };
    }

    const padding = 8;
    const isLast = stepIdx === chapter.steps.length - 1 && (!fullTour || chIdx === chapters.length - 1);
    return (
        <div className="fixed inset-0 z-[70] pointer-events-none" data-testid="onboarding-tour">
            <svg className="absolute inset-0 w-full h-full pointer-events-auto" onClick={next}>
                <defs>
                    <mask id="tour-mask">
                        <rect x="0" y="0" width="100%" height="100%" fill="white" />
                        {rect && (
                            <rect x={Math.max(0, rect.left - padding)} y={Math.max(0, rect.top - padding)}
                                width={rect.width + padding * 2} height={rect.height + padding * 2} rx="6" fill="black" />
                        )}
                    </mask>
                </defs>
                <rect x="0" y="0" width="100%" height="100%" fill="rgba(0,0,0,0.7)" mask="url(#tour-mask)" />
                {rect && (
                    <rect x={Math.max(0, rect.left - padding)} y={Math.max(0, rect.top - padding)}
                        width={rect.width + padding * 2} height={rect.height + padding * 2} rx="6"
                        fill="none" stroke="#FFCC00" strokeWidth="3" className="animate-pulse" />
                )}
            </svg>

            <div className="absolute bg-white border-2 border-[#FFCC00] rounded-sm shadow-2xl pointer-events-auto w-[380px] max-w-[calc(100vw-24px)]"
                style={ttStyle} data-testid="tour-tooltip">
                <div className="px-4 py-3 bg-[#FFCC00]/15 flex items-center justify-between border-b border-[#FFCC00]/40">
                    <div className="flex items-center gap-2 min-w-0">
                        <Sparkles className="w-4 h-4 text-[#111827] shrink-0" />
                        <span className="text-[10px] uppercase tracking-widest font-bold text-gray-700 truncate">{chapter.title}</span>
                        <span className="font-mono-num text-[10px] font-bold bg-[#111827] text-[#FFCC00] px-1.5 py-0.5 rounded-sm shrink-0">
                            {fullTour ? `${doneSteps + 1} / ${totalSteps}` : `${stepIdx + 1} / ${chapter.steps.length}`}
                        </span>
                    </div>
                    <button onClick={quit} className="text-gray-500 hover:text-[#111827]" data-testid="tour-skip"><X className="w-4 h-4" /></button>
                </div>
                <div className="px-5 py-4">
                    <h3 className="font-display font-black text-base mb-1.5">{step.title}</h3>
                    <p className="text-sm text-gray-700 leading-relaxed">{step.body}</p>
                </div>
                <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
                    <button onClick={() => { setActive(false); setPicker(true); setRect(null); }}
                        className="text-[10px] font-bold uppercase tracking-wider text-gray-500 hover:text-[#111827]" data-testid="tour-back-to-chapters">
                        Kapitel-Übersicht
                    </button>
                    <div className="flex items-center gap-2">
                        {(stepIdx > 0 || (fullTour && chIdx > 0)) && (
                            <Button size="sm" variant="outline" onClick={prev} className="rounded-sm" data-testid="tour-prev">
                                <ChevronLeft className="w-3.5 h-3.5" /> Zurück
                            </Button>
                        )}
                        <Button size="sm" onClick={next} className="bg-[#111827] hover:bg-black text-[#FFCC00] font-bold rounded-sm uppercase tracking-wider" data-testid="tour-next">
                            {isLast ? (<><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Fertig</>) : (<>Weiter <ChevronRight className="w-3.5 h-3.5 ml-1" /></>)}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}

/** Trigger-Helper, kann von einem „Tour neu starten“-Menü-Eintrag aufgerufen werden. */
export function restartOnboardingTour(role) {
    try {
        if (role) localStorage.removeItem(`${STORAGE_PREFIX}${role}`);
    } catch { /* ignore */ }
    window.dispatchEvent(new CustomEvent("twowheels:start-tour"));
}
